import React from 'react';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import type { LangfuseTrace } from '@/types/traces';
import {
  processDailyVolume,
  processHourlyDistribution
} from '@/utils/traceAnalytics';

interface ClientDashboardChartsProps {
  traces: LangfuseTrace[];
  isLoading?: boolean;
}

// Componente para gráfica de volumen diario
const DailyVolumeChart: React.FC<{ traces: LangfuseTrace[] }> = ({ traces }) => {
  const data = React.useMemo(() => processDailyVolume(traces, 30), [traces]);

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit' });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Volumen de Llamadas (30 días)</CardTitle>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={300}>
          <AreaChart data={data} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="callsGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
            <XAxis 
              dataKey="date" 
              tickFormatter={formatDate}
              tick={{ fontSize: 12 }}
            />
            <YAxis tick={{ fontSize: 12 }} />
            <Tooltip 
              labelFormatter={(label) => `Fecha: ${formatDate(label)}`}
              formatter={(value: any) => [`${value} llamadas`, 'Llamadas']}
              contentStyle={{ 
                backgroundColor: 'white',
                border: '1px solid #e5e7eb',
                borderRadius: '6px',
                fontSize: '12px'
              }}
            />
            <Area
              type="monotone"
              dataKey="calls"
              stroke="#3b82f6"
              fillOpacity={1}
              fill="url(#callsGradient)"
              strokeWidth={2}
            />
          </AreaChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
};

// Componente para distribución por horas
const HourlyDistributionChart: React.FC<{ traces: LangfuseTrace[] }> = ({ traces }) => {
  const data = React.useMemo(() => processHourlyDistribution(traces), [traces]);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Distribución por Horas</CardTitle>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={data} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
            <XAxis 
              dataKey="hour" 
              tick={{ fontSize: 12 }}
              interval={1}
            />
            <YAxis tick={{ fontSize: 12 }} />
            <Tooltip 
              formatter={(value: any) => [`${value} llamadas`, 'Llamadas']}
              contentStyle={{ 
                backgroundColor: 'white',
                border: '1px solid #e5e7eb',
                borderRadius: '6px',
                fontSize: '12px'
              }}
            />
            <Bar 
              dataKey="calls" 
              fill="#10b981" 
              radius={[2, 2, 0, 0]}
              opacity={0.8}
            />
          </BarChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
};

// Componente para tendencia dual (volumen + duración)
const DualTrendChart: React.FC<{ traces: LangfuseTrace[] }> = ({ traces }) => {
  const data = React.useMemo(() => processDailyVolume(traces, 14), [traces]);

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit' });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Tendencia: Volumen y Duración</CardTitle>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={data} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
            <XAxis 
              dataKey="date" 
              tickFormatter={formatDate}
              tick={{ fontSize: 12 }}
            />
            <YAxis yAxisId="calls" orientation="left" tick={{ fontSize: 12 }} />
            <YAxis yAxisId="duration" orientation="right" tick={{ fontSize: 12 }} />
            <Tooltip 
              labelFormatter={(label) => `Fecha: ${formatDate(label)}`}
              formatter={(value: any, name: string) => [
                name === 'calls' ? `${value} llamadas` : `${value.toFixed(1)}s`,
                name === 'calls' ? 'Llamadas' : 'Duración Promedio'
              ]}
              contentStyle={{ 
                backgroundColor: 'white',
                border: '1px solid #e5e7eb',
                borderRadius: '6px',
                fontSize: '12px'
              }}
            />
            <Line
              yAxisId="calls"
              type="monotone"
              dataKey="calls"
              stroke="#3b82f6"
              strokeWidth={2}
              dot={{ r: 4 }}
              name="calls"
            />
            <Line
              yAxisId="duration"
              type="monotone"
              dataKey="avgDuration"
              stroke="#f59e0b"
              strokeWidth={2}
              dot={{ r: 4 }}
              name="avgDuration"
            />
          </LineChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
};

// Componente principal simplificado
export const ClientDashboardCharts: React.FC<ClientDashboardChartsProps> = ({ 
  traces, 
  isLoading = false 
}) => {
  if (isLoading) {
    return (
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        {Array.from({ length: 3 }, (_, i) => (
          <Card key={i}>
            <CardHeader>
              <div className="h-6 bg-gray-200 animate-pulse rounded"></div>
            </CardHeader>
            <CardContent>
              <div className="h-[300px] bg-gray-100 animate-pulse rounded"></div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (!traces || traces.length === 0) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center h-64">
          <div className="text-center">
            <p className="text-gray-500 mb-2">No hay datos disponibles</p>
            <p className="text-sm text-gray-400">Las gráficas aparecerán cuando haya llamadas registradas</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Tendencia principal */}
      <div className="grid grid-cols-1 gap-6">
        <DailyVolumeChart traces={traces} />
      </div>
      
      {/* Gráficas de análisis */}
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        <HourlyDistributionChart traces={traces} />
        <DualTrendChart traces={traces} />
      </div>
    </div>
  );
};

export default ClientDashboardCharts;